import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../auth/AuthContext';

const API_BASE = import.meta.env.VITE_API_URL || `http://${window.location.hostname}:7070`;

export default function VerifyEmailPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token: authToken, login, user } = useAuth();
  const verifyToken = searchParams.get('token');
  const [status, setStatus] = useState(verifyToken ? 'verifying' : 'pending');
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (!verifyToken) return;
    fetch(`${API_BASE}/api/v1/auth/verify-email?token=${encodeURIComponent(verifyToken)}`)
      .then(r => {
        if (r.ok) {
          setStatus('success');
          // Re-login to get fresh JWT with emailVerified=true
          if (authToken) {
            const refreshToken = localStorage.getItem('vq_refresh_token') || sessionStorage.getItem('vq_refresh_token');
            if (refreshToken) {
              fetch(`${API_BASE}/api/v1/auth/refresh`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ refreshToken }),
              })
                .then(r2 => r2.ok ? r2.json() : null)
                .then(data => {
                  if (data) login(data.accessToken, data.refreshToken, !!localStorage.getItem('vq_refresh_token'));
                })
                .catch(() => {});
            }
          }
        } else {
          return r.json().then(data => setStatus(data.error === 'TOKEN_EXPIRED' ? 'expired' : 'error'));
        }
      })
      .catch(() => setStatus('error'));
  }, [verifyToken]);

  const resend = async () => {
    if (!authToken) return;
    setResending(true);
    try {
      const res = await fetch(`${API_BASE}/api/v1/auth/resend-verification`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setStatus(res.ok ? 'resent' : 'error');
    } catch {
      setStatus('error');
    } finally {
      setResending(false);
    }
  };

  return (
    <div style={s.page}>
      <div style={s.card}>
        {status === 'verifying' && (
          <>
            <div style={s.icon}>...</div>
            <h1 style={s.title}>{t('verifyEmail.verifyingTitle')}</h1>
            <p style={s.sub}>{t('verifyEmail.verifyingSubtitle')}</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div style={s.icon}>&#x2705;</div>
            <h1 style={s.title}>{t('verifyEmail.successTitle')}</h1>
            <p style={s.sub}>{t('verifyEmail.successSubtitle')}</p>
            <button style={s.btn} onClick={() => navigate('/admin/queue')}>
              {t('verifyEmail.goToDashboard')}
            </button>
          </>
        )}

        {status === 'pending' && (
          <>
            <div style={s.icon}>&#x2709;&#xFE0F;</div>
            <h1 style={s.title}>{t('verifyEmail.pendingTitle')}</h1>
            <p style={s.sub}>
              {t('verifyEmail.pendingSubtitle')}
            </p>
            <button style={s.btn} onClick={resend} disabled={resending}>
              {resending ? t('verifyEmail.resending') : t('verifyEmail.resend')}
            </button>
          </>
        )}

        {status === 'resent' && (
          <>
            <div style={s.icon}>&#x2709;&#xFE0F;</div>
            <h1 style={s.title}>{t('verifyEmail.resentTitle')}</h1>
            <p style={s.sub}>{t('verifyEmail.resentSubtitle')}</p>
            <button style={s.btn} onClick={resend} disabled={resending}>
              {resending ? t('verifyEmail.resending') : t('verifyEmail.resendAgain')}
            </button>
          </>
        )}

        {status === 'expired' && (
          <>
            <div style={s.icon}>&#x23F0;</div>
            <h1 style={s.title}>{t('verifyEmail.expiredTitle')}</h1>
            <p style={s.sub}>{t('verifyEmail.expiredSubtitle')}</p>
            <button style={s.btn} onClick={resend} disabled={resending}>
              {resending ? t('verifyEmail.resending') : t('verifyEmail.resend')}
            </button>
          </>
        )}

        {status === 'error' && (
          <>
            <div style={s.icon}>&#x274C;</div>
            <h1 style={s.title}>{t('verifyEmail.failedTitle')}</h1>
            <p style={s.sub}>{t('verifyEmail.failedSubtitle')}</p>
            <button style={s.btn} onClick={() => navigate('/admin/login')}>
              {t('verifyEmail.goToLogin')}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

const s = {
  page: {
    minHeight: '100dvh', background: '#0F172A', display: 'flex',
    justifyContent: 'center', alignItems: 'flex-start', padding: '48px 16px',
    fontFamily: 'system-ui, sans-serif',
  },
  card: {
    background: '#1E293B', borderRadius: 20, boxShadow: '0 4px 24px rgba(0,0,0,0.25)',
    padding: '40px 28px', width: '100%', maxWidth: 420, textAlign: 'center',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12,
    border: '1px solid #334155',
  },
  icon: { fontSize: '3rem' },
  title: { fontSize: '1.5rem', fontWeight: 700, margin: 0, color: '#F8FAFC' },
  sub: { fontSize: '0.95rem', color: '#94A3B8', margin: 0, lineHeight: 1.5 },
  btn: {
    marginTop: 8, fontSize: '1rem', fontWeight: 600, padding: '13px 24px', borderRadius: 12,
    border: 'none', background: 'linear-gradient(135deg, #7C3AED, #06B6D4)', color: '#fff', cursor: 'pointer', width: '100%',
  },
};
